import { useMutation, useQueryClient } from '@tanstack/react-query'
import { message } from '@surecompanies/core_components'
import { deleteDocument } from '../api/plan-override-documents-api'
import type { DocumentResponseDto } from '../api/dto/plan-override-document.dto'
import { DOCUMENTS_QUERY_KEY } from './useOverrideDocuments'
import { HISTORY_QUERY_KEY } from './useOverrideHistory'

export function useDeleteOverrideDocument(ruleId: string, ruleBase?: string) {
  const queryClient = useQueryClient()

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (documentId: string) =>
      deleteDocument(ruleId, documentId, ruleBase),

    onMutate: async (documentId) => {
      await queryClient.cancelQueries({
        queryKey: [DOCUMENTS_QUERY_KEY, ruleId, ruleBase],
      })

      const previous = queryClient.getQueryData<DocumentResponseDto[]>([
        DOCUMENTS_QUERY_KEY,
        ruleId,
        ruleBase,
      ])

      // Optimistically remove from list
      queryClient.setQueryData<DocumentResponseDto[]>(
        [DOCUMENTS_QUERY_KEY, ruleId, ruleBase],
        (old) => old?.filter((d) => d.id !== documentId) ?? []
      )

      return { previous }
    },

    onError: (err, _documentId, ctx) => {
      // eslint-disable-next-line no-console
      console.error('delete override document failed', err)
      if (ctx?.previous !== undefined) {
        queryClient.setQueryData(
          [DOCUMENTS_QUERY_KEY, ruleId, ruleBase],
          ctx.previous
        )
      }
      message.error('Failed to remove document. Please try again.')
    },

    onSuccess: () => {
      message.success('Document removed.')
    },

    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [DOCUMENTS_QUERY_KEY, ruleId, ruleBase],
        }),
        queryClient.invalidateQueries({
          queryKey: [HISTORY_QUERY_KEY, ruleId],
        }),
      ])
    },
  })

  return { deleteDocument: mutateAsync, isDeleting: isPending }
}
